import React, { useCallback, useMemo, useState } from 'react';
import {
  OfferType,
  useCurrentOrganizationId,
  useOffers,
  usePlanOffers,
  useAddOfferToPlan,
  Offer,
  Session,
  usePriceTiers,
} from '@app/api';
import { Theme } from '@app/theme';
import { Button, CenteredModal, useToast } from '@app/core';
import { OfferPriceTierBlock } from 'components/Offers/OfferPriceTierBlock';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  session: Session;
}
const useStyles = createUseStyles((theme: Theme) => ({
  offerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
}));

export const ChangePriceTierModal = ({
  isOpen,
  onRequestClose,
  session,
}: Props) => {
  const organizationId = useCurrentOrganizationId(true);
  const classes = useStyles();
  const toast = useToast();
  const { t } = useTranslation();

  const { data: allOffers } = useOffers();
  const { data: planOffers } = usePlanOffers(session.planId);
  const { mutateAsync: updateOffer } = useAddOfferToPlan(session.planId);
  const { data: priceThiers } = usePriceTiers();
  const duration = session.duration;
  const [selectedOffer, selectOffer] = useState(undefined as Offer | undefined);

  const close = useCallback(() => {
    selectOffer(undefined);
    onRequestClose();
  }, [onRequestClose, selectOffer]);

  const currentOffer = useMemo(
    () => planOffers?.find((o) => o.type === OfferType.TARGETING),
    [planOffers],
  );

  const availableOffers = useMemo(() => {
    if (!allOffers || !priceThiers) return [];

    const priceThiersMinimum: Record<string, number> = {};
    for (const priceThier of priceThiers) {
      priceThiersMinimum[priceThier.id] = priceThier.minimum;
    }

    const { studyType, studySubject, format } = session;

    const res = allOffers
      .filter(({ name, requirement, type, products }) => {
        return (
          requirement.studyTypes.includes(studyType) &&
          requirement.studySubjects.includes(studySubject) &&
          (!requirement.formats ||
            !format ||
            requirement.formats.includes(format)) &&
          name === currentOffer?.name &&
          !!requirement.priceThierId &&
          type === OfferType.TARGETING &&
          products.find((p) => !p.forRecruitmentByClient)
        );
      })
      .sort((offer1, offer2) => {
        const thierId1 = offer1.requirement.priceThierId!;
        const thierId2 = offer2.requirement.priceThierId!;
        return priceThiersMinimum[thierId1] - priceThiersMinimum[thierId2];
      });
    return res;
  }, [session, allOffers, currentOffer, priceThiers]);

  const onSubmit = useCallback(async () => {
    try {
      if (!selectedOffer || selectedOffer.id === currentOffer?.id) {
        return;
      }
      await updateOffer({
        offerId: selectedOffer.id,
      });
      close();
      toast.saved();
    } catch (error) {
      toast.exception(error);
    }
  }, [close, selectedOffer, updateOffer, session]);

  return (
    <CenteredModal
      title={t('editSession.upsell.priceThier')}
      isOpen={isOpen && !!organizationId}
      onRequestClose={close}
      buttons={[
        <Button color="ink" variant="muted" onClick={close} key="cancel" small>
          {t('editSession.upsell.cancel')}
        </Button>,
        [
          <Button color="green" onClick={onSubmit} key="submit" small>
            {t('editSession.upsell.confirm')}
          </Button>,
        ],
      ]}
    >
      <div className={classes.offerList}>
        {availableOffers.map((offer) => (
          <OfferPriceTierBlock
            key={offer.id}
            offer={offer}
            selected={(selectedOffer || currentOffer)?.id === offer.id}
            onSelect={() => selectOffer(offer)}
            duration={duration}
          />
        ))}
      </div>
    </CenteredModal>
  );
};
